import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { getFAQ } from '@/lib/api';

const FAQItem = ({ item, index, isOpen, onToggle }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="border-b border-terracotta-light last:border-b-0"
    >
      <button
        onClick={onToggle}
        className="w-full py-5 flex items-center justify-between text-left hover:text-terracotta transition-colors"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-medium text-charcoal pr-4">{item.question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-terracotta flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-stone flex-shrink-0" />
        )}
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="pb-5 text-stone leading-relaxed">{item.answer}</p>
      </motion.div>
    </motion.div>
  );
};

const FAQPage = () => {
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItems, setOpenItems] = useState({});

  useEffect(() => {
    const loadFAQ = async () => {
      try {
        const res = await getFAQ();
        setFaqItems(res.data);
        // Open the first item by default
        if (res.data.length > 0) {
          setOpenItems({ 0: true });
        }
      } catch (error) {
        console.error('Error loading FAQ:', error);
      } finally {
        setLoading(false);
      }
    };
    loadFAQ();
  }, []);

  const toggleItem = (index) => {
    setOpenItems(prev => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative py-20 md:py-32 bg-terracotta-light/20 overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <HelpCircle className="w-12 h-12 text-terracotta mx-auto mb-6" />
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-charcoal mb-6" data-testid="faq-title">
              Questions fréquentes
            </h1>
            <p className="text-stone text-lg max-w-xl mx-auto">
              Retrouvez ici les réponses aux questions les plus courantes concernant notre mariage.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="section-padding bg-white">
        <div className="max-w-3xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-stone">Chargement...</div>
            </div>
          ) : faqItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-stone">Aucune question fréquente pour le moment.</p>
            </div>
          ) : (
            <div className="card-elegant">
              {faqItems.map((item, index) => (
                <FAQItem
                  key={item.id}
                  item={item}
                  index={index}
                  isOpen={openItems[index]}
                  onToggle={() => toggleItem(index)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-bone">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <MessageCircle className="w-10 h-10 text-terracotta mx-auto mb-4" />
            <h2 className="font-display text-2xl md:text-3xl text-charcoal mb-4">
              Une autre question ?
            </h2>
            <p className="text-stone mb-6">
              Connectez-vous à votre espace invité pour nous poser directement vos questions.
            </p>
            <Link to="/espace-invite">
              <Button data-testid="guest-space-btn" className="btn-primary">
                Accéder à mon espace
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default FAQPage;
